/**
 * KAN-744: менеджеры организации - живые люди, которые доводят клиентов до
 * продажи (не аккаунты). Справочник /api/v1/managers/, закрепление за
 * клиентом и заказом, карточка со сводкой и воронкой его клиентов.
 */

/** Краткая карточка менеджера в клиенте и заказе (поле manager). */
export interface ManagerBrief {
  id: string;
  full_name: string;
  /** KAN-828: старший менеджер (видит всех клиентов, переназначает любых). */
  is_senior?: boolean;
}

/**
 * KAN-837: когда менеджеру можно звонить: дни недели (1 = Пн .. 7 = Вс) и
 * время "HH:MM" в часовом поясе организации. Пустой объект: не задано.
 */
export interface WorkHours {
  days?: number[];
  start?: string;
  end?: string;
}

/** Строка справочника менеджеров. */
export interface Manager extends ManagerBrief {
  last_name: string;
  first_name: string;
  middle_name: string;
  phone: string;
  notes: string;
  /** false: в архиве (не предлагается для закрепления). */
  is_active: boolean;
  clients_count: number;
  sales_count: number;
  /** Конверсия клиентов менеджера в продажу, %. */
  conversion_pct: number;
  /** KAN-745: процент с продаж 0..100; null: не задан. */
  commission_pct: number | null;
  /** KAN-745: к выплате = сумма заказов x процент / 100; null: процент не задан. */
  payout: number | null;
  created_at: string | null;
  /** KAN-829: Telegram менеджера привязан к боту для менеджеров. */
  telegram_linked: boolean;
  /** KAN-829: когда привязан; null: не привязан. */
  telegram_linked_at: string | null;
  /** KAN-837: когда можно звонить; нет у старого backend. */
  work_hours?: WorkHours;
  /** KAN-847: линия входящих звонков 1..3; null: не принимает; нет у старого backend. */
  call_line?: ManagerCallLine | null;
}

/** KAN-847: линии входящих звонков на номер кабинета (сначала 1, потом 2, потом 3). */
export type ManagerCallLine = 1 | 2 | 3;

/** Сводка карточки менеджера (GET /managers/<id>/). */
export interface ManagerSummary {
  clients: number;
  sales: number;
  conversion_pct: number;
  refused: number;
  orders_count: number;
  orders_sum: number;
  /** KAN-745: процент с продаж; null: не задан. */
  commission_pct: number | null;
  /** KAN-745: к выплате; null: процент не задан. */
  payout: number | null;
}

/** KAN-811: вход менеджера в кабинет (поле account карточки). */
export interface ManagerAccount {
  email: string;
  /** false: вход отключён владельцем. */
  is_active: boolean;
  /**
   * KAN-834: вход создан ссылкой приложения (служебный email, без пароля):
   * email не показывается, пароль не меняется. Нет у старого backend.
   */
  app_only?: boolean;
  /**
   * KAN-846: чем менеджер входит: настоящая почта, иначе телефон (вход с
   * паролем без почты), иначе пусто (вход только по ссылке).
   */
  login?: string;
}

/**
 * KAN-811: тело создания входа. KAN-846: email необязателен, если у менеджера
 * есть телефон; входу через приложение (без пароля) этот же запрос задаёт пароль.
 */
export interface ManagerAccountCreateBody {
  email?: string;
  password: string;
}

/** KAN-811: тело смены пароля. */
export interface ManagerAccountUpdateBody {
  password: string;
}

export interface ManagerDetail extends Manager {
  summary: ManagerSummary;
  /** KAN-811: вход в кабинет; null: входа нет (старый backend поле не отдаёт). */
  account?: ManagerAccount | null;
}

/** Фильтр списка: активные, архив или все. */
export type ManagerActiveFilter = 'true' | 'false' | 'all';

/** Тело POST (обязательно только имя, KAN-745) и PATCH (любые поля). */
export interface ManagerBody {
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  phone?: string;
  notes?: string;
  is_active?: boolean;
  /** KAN-828: старший менеджер (меняют владелец и оператор). */
  is_senior?: boolean;
  /** KAN-745: процент с продаж 0..100; null: снять процент. */
  commission_pct?: number | null;
  /** KAN-837: когда можно звонить; {} снимает. */
  work_hours?: WorkHours;
  /** KAN-847: линия входящих звонков (только владелец); null: не принимает. */
  call_line?: ManagerCallLine | null;
}

/** KAN-837: свой профиль менеджера (GET/PATCH /managers/me/). */
export interface ManagerSelfProfile {
  id: string;
  last_name: string;
  first_name: string;
  middle_name: string;
  phone: string;
  work_hours: WorkHours;
  /** Часовой пояс организации (IANA), в нём считается время звонков. */
  organization_timezone: string;
  /** KAN-846: вход по ссылке без пароля, менеджер должен задать логин и пароль. */
  needs_credentials?: boolean;
  /** KAN-846: настоящая почта входа или пустая строка. */
  login_email?: string;
}

/** KAN-846: тело POST /managers/me/credentials/. */
export interface ManagerCredentialsBody {
  email?: string;
  password: string;
  password_confirm: string;
}

/** KAN-837: тело PATCH /managers/me/ (любые поля). */
export interface ManagerSelfBody {
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  phone?: string;
  work_hours?: WorkHours;
}

/** Ответ 409 на удаление менеджера с клиентами. */
export const MANAGER_IN_USE_CODE = 'manager_in_use';
